import { getDateLocale as getInterfaceDateLocale } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { FlaskConical, Code, XCircle, Timer, CalendarIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { HeadlineOverlay, HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { CopyCodeModal } from '@/components/shared/CopyCodeModal';
import { AlertDialog, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { deleteHeadlineTest } from '@/services/headlineTestService';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';

interface HeadlineActiveTestPanelProps {
  headlineOverlay: HeadlineOverlay;
  variations: HeadlineOverlayVariation[];
  onFinalize: () => void;
  getTrafficPercentage: (variationId: string) => number;
  headlineTestId?: string;
  token?: string;
  videoId?: string;
}

export function HeadlineActiveTestPanel({
  headlineOverlay,
  variations,
  onFinalize,
  getTrafficPercentage,
  headlineTestId,
  token,
  videoId
}: HeadlineActiveTestPanelProps) {
  useLanguage();
  const { user } = useAuth();
  const [showCopyCodeModal, setShowCopyCodeModal] = useState(false);
  const [showFinalizeModal, setShowFinalizeModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { abTestConfig } = headlineOverlay;

  // Calcular progresso do teste
  const calculateTestProgress = () => {
    const startDate = abTestConfig.startDate;
    const endDate = abTestConfig.endDate;
    
    if (!startDate) {
      return { percentage: 0, daysRemaining: null, daysTotal: null, daysRunning: 0, isIndefinite: true };
    }
    
    const start = new Date(startDate);
    const now = new Date();
    const daysSinceStart = Math.floor((now.getTime() - start.getTime()) / (1000 * 60 * 60 * 24));
    
    if (!endDate) {
      return { 
        percentage: null, 
        daysRemaining: null, 
        daysTotal: null,
        daysRunning: daysSinceStart,
        isIndefinite: true 
      };
    }
    
    const end = new Date(endDate);
    const totalDuration = end.getTime() - start.getTime();
    const elapsed = now.getTime() - start.getTime();
    
    const percentage = Math.min(100, Math.max(0, (elapsed / totalDuration) * 100));
    const daysRemaining = Math.max(0, Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)));
    const daysTotal = Math.ceil(totalDuration / (1000 * 60 * 60 * 24));
    
    return { percentage, daysRemaining, daysTotal, daysRunning: daysSinceStart, isIndefinite: false };
  };

  const testProgress = calculateTestProgress();

  const handleFinalizeConfirm = async () => {
    if (!headlineTestId || !token) {
      onFinalize();
      setShowFinalizeModal(false);
      return;
    }

    setIsDeleting(true);

    try {
      await deleteHeadlineTest(headlineTestId, token);

      toast.success(translateInterfaceText("Teste finalizado e deletado com sucesso!"));
      onFinalize();
      setShowFinalizeModal(false);
    } catch (error: any) {
      console.error('Erro ao deletar teste:', error);
      toast.error(error.response?.data?.message || translateInterfaceText("Erro ao deletar teste"));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-full py-4 space-y-5">
      {/* Card: Testes em Andamento */}
      <div className="rounded-xl border border-border bg-background p-4">
        {/* Badge Testes em andamento - Azul */}
        <div className="flex items-center gap-2 mb-4">
          <Badge className="bg-primary/10 text-primary border border-primary/20 gap-1.5 font-medium text-xs px-2.5 py-1 hover:bg-primary/15">
            <FlaskConical className="h-3.5 w-3.5" />
            {translateInterfaceText("Testes em andamento")}</Badge>
        </div>

        {/* Lista de Variações */}
        <div className="space-y-3 mb-4">
          <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            {translateInterfaceText("Variações em teste")}</h4>
          <div className="space-y-2">
            {variations.map((variation, index) => (
              <div 
                key={variation.id}
                className="flex items-center gap-2 p-2.5 rounded-lg bg-muted/30"
              >
                <Badge 
                  variant="outline" 
                  className="text-[12px] px-2.5 py-1 h-auto bg-white text-[#6B7280] border-[#E5E7EB] font-medium rounded-full"
                >
                  {index === 0 ? translateInterfaceText("Principal") : translateInterfaceText("Em teste")}
                </Badge>
                <span className="text-sm font-medium text-foreground truncate max-w-[150px]">
                  {variation.name || translateInterfaceText("Variação {value0}", { value0: (String.fromCharCode(65 + index)) })}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Distribuição de Tráfego - Premium */}
        <div className="space-y-3 mb-4">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              {translateInterfaceText("Distribuição de tráfego")}</h4>
            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-md">
              {translateInterfaceText("Total: 100%")}</span>
          </div>
          
          <div className="space-y-3">
            {variations.map((variation, index) => {
              const percent = getTrafficPercentage(variation.id);
              
              return (
                <div key={variation.id} className="space-y-1.5">
                  {/* Nome da variação acima da barra */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">
                      {translateInterfaceText("Variação")}{" "}{String.fromCharCode(65 + index)}
                    </span>
                    <span className="text-sm font-semibold text-foreground tabular-nums">
                      {percent}%
                    </span>
                  </div>
                  
                  {/* Barra premium com thumb */}
                  <div className="relative h-1 bg-border/50 rounded-full overflow-visible">
                    {/* Barra preenchida */}
                    <div 
                      className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-primary to-primary/80 transition-all duration-300"
                      style={{ width: `${percent}%` }}
                    />
                    
                    {/* Thumb circular */}
                    <div 
                      className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-primary shadow-sm"
                      style={{ left: `calc(${percent}% - 6px)` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Progresso do Teste */}
        <Card className="border-[#E4E7EC] bg-[#FAFBFC]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-primary" />
                <span className="font-medium text-sm text-foreground">{translateInterfaceText("Progresso do Teste")}</span>
              </div>
              
              {testProgress.isIndefinite ? (
                <Badge variant="outline" className="bg-muted text-muted-foreground border-border">
                  {testProgress.daysRunning} {testProgress.daysRunning === 1 ? translateInterfaceText("dia") : translateInterfaceText("dias")} {" "}{translateInterfaceText("em execução")}</Badge>
              ) : (
                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                  {testProgress.daysRemaining} {testProgress.daysRemaining === 1 ? translateInterfaceText("dia") : translateInterfaceText("dias")} {" "}{translateInterfaceText("restantes")}</Badge>
              )}
            </div>
            
            {!testProgress.isIndefinite && testProgress.percentage !== null ? (
              <>
                <Progress 
                  value={testProgress.percentage || 0}
                  className="mb-3 h-1.5"
                />
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>
                    {translateInterfaceText("Iniciado:")}{" "}{abTestConfig.startDate && format(new Date(abTestConfig.startDate), "dd/MM", { locale: getInterfaceDateLocale() })}
                  </span>
                  <span className="font-medium text-foreground">{Math.round(testProgress.percentage)}%</span>
                  <span>
                    {translateInterfaceText("Término:")}{" "}{abTestConfig.endDate && format(new Date(abTestConfig.endDate), "dd/MM", { locale: getInterfaceDateLocale() })}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <CalendarIcon className="h-4 w-4 text-primary" />
                <span>
                  {translateInterfaceText("Iniciado em:")}{" "}{abTestConfig.startDate
                    ? format(new Date(abTestConfig.startDate), "PPP", { locale: getInterfaceDateLocale() })
                    : format(new Date(), "PPP", { locale: getInterfaceDateLocale() })}
                </span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Ações */}
      <div className="border-t border-border pt-4">
        <div className="flex justify-end gap-3">
          <TooltipProvider delayDuration={300}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  onClick={() => setShowCopyCodeModal(true)}
                  className="gap-2 !h-auto self-stretch px-[18px] rounded-xl font-semibold text-sm shadow-lg transition-all duration-200 border-0"
                  size="sm"
                  style={{
                    background: 'linear-gradient(135deg, #2D6CF6 0%, #1E5FE6 100%)',
                    color: '#FFFFFF',
                    boxShadow: '0 4px 12px rgba(45, 108, 246, 0.3)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-1px)';
                    e.currentTarget.style.boxShadow = '0 6px 20px rgba(45, 108, 246, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(45, 108, 246, 0.3)';
                  }}
                >
                  <Code className="h-[18px] w-[18px]" />
                  {translateInterfaceText("Copiar código")}</Button>
              </TooltipTrigger>
              <TooltipContent
                side="bottom"
                align="center"
                className="bg-gray-900 text-white text-xs px-3 py-1.5"
              >
                {translateInterfaceText("Copiar código de incorporação do vídeo")}</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <Button
            variant="destructive"
            onClick={() => setShowFinalizeModal(true)}
            className="gap-2"
          >
            <XCircle className="h-4 w-4" />
            {translateInterfaceText("Finalizar Teste")}</Button>
        </div>
      </div>

      {/* Modal de Copiar Código */}
      <CopyCodeModal
        isOpen={showCopyCodeModal}
        onClose={() => setShowCopyCodeModal(false)}
        entityType="video"
        entityId={videoId || ''}
        entityName="Teste A/B Headlines"
        userId={user?.id || user?.id_user}
        videoOrigin="TESTHEADLINE"
        hideJsTab={true}
      />

      {/* Modal de Finalização */}
      <AlertDialog open={showFinalizeModal} onOpenChange={setShowFinalizeModal}>
        <AlertDialogContent className="w-[92vw] sm:w-[clamp(360px,38vw,440px)] sm:max-w-none rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>{translateInterfaceText("Finalizar teste A/B?")}</AlertDialogTitle>
            <AlertDialogDescription>
              {translateInterfaceText("O teste será encerrado e a variação principal será aplicada. Esta ação não pode ser desfeita.")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowFinalizeModal(false)}
              disabled={isDeleting}
            >
              {translateInterfaceText("Cancelar")}</Button>
            <Button
              variant="destructive"
              onClick={handleFinalizeConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? translateInterfaceText("Deletando...") : translateInterfaceText("Finalizar Teste")}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
